import React, { useMemo, useState } from 'react';

import { GroupedBars, Legend, LineChart, Phasors } from './charts';
import { DiagramKey, Wire } from './diagrams';
import { Foot, fmt, Grid, Kpi, KpiRow, Note, Panel, Pill, plural, Readout, Row, Segmented, Select, Table } from './parts';
import {
    clock, eventCapture, eventEnvelope, eventsFor, EVENT_TYPES, harmonicOf, NEUTRAL_COLOR,
    ORDERS, orderGroup, PHASES, PHASE_COLOR, stamp
} from './model';
import { downloadCsv } from './exports';

const phaseOptions = [
    { value: 'all', label: 'All phases' },
    ...PHASES.map(name => ({ value: name, label: name }))
];

const IL = () => <>I<sub>L</sub></>;

/* ── The conversion path ────────────────────────────────────── */

function PathDiagram({ ac, onSelect }) {
    const stations = [
        { id: 'incomer', title: 'Utility incomer' },
        { id: 'rectifier', title: 'UPS rectifier input' },
        { id: 'link' },
        { id: 'ups', title: 'UPS inverter output' },
        { id: 'pdu', title: 'PDU / rack feed' }
    ];

    const stage = { incomer: 'AC supply', rectifier: 'AC → DC', ups: 'DC → AC', pdu: 'AC loads' };
    const onBattery = ac.batteryState === 'Discharging';

    /* Whether power is passing through a stage, and so along the wire into it. */
    const carrying = id => (id === 'link' ? ac.dcVolts > 0 && ac.powers.ups > 0 : ac.powers[id] > 0);

    const wire = (id, i) => {
        return <span className="dash-flow__link"><Wire off={!(carrying(stations[i - 1].id) && carrying(id))} /></span>;
    };

    return (
        <div className="dash-flow" style={{ '--wire': 'var(--accent)' }}>
            <div className="dash-flow__bypass">
                <button
                    type="button"
                    className="dash-flow__box dash-flow__box--side"
                    aria-pressed={ac.meter.id === 'bypass'}
                    onClick={() => onSelect('bypass')}
                >
                    <span className="dash-flow__name">Static bypass</span>
                    <span className="dash-flow__value">{fmt(ac.pathVolts.bypass)}<small>V L–L</small></span>
                    <span className="dash-flow__stage">{ac.dead.includes('bypass') ? 'No supply' : 'Standby · 0 kW'}</span>
                </button>
            </div>

            <ol className="dash-flow__chain">
                {stations.map((station, i) => (
                    station.id === 'link' ? (
                        <li key="link">
                            {wire('link', i)}
                            <div className="dash-flow__box dash-flow__box--link">
                                <span className="dash-flow__name">DC link</span>
                                <span className="dash-flow__value">{fmt(ac.dcVolts)}<small>VDC</small></span>
                                <span className="dash-flow__stage">+/− bus · UPS interface</span>
                            </div>
                        </li>
                    ) : (
                        <li key={station.id}>
                            {i > 0 && wire(station.id, i)}
                            <button
                                type="button"
                                className={`dash-flow__box${ac.dead.includes(station.id) ? ' is-dead' : ''}`}
                                aria-pressed={ac.meter.id === station.id}
                                onClick={() => onSelect(station.id)}
                            >
                                <span className="dash-flow__name">{station.title}</span>
                                <span className="dash-flow__value">{fmt(ac.pathVolts[station.id])}<small>V L–L</small></span>
                                <span className="dash-flow__stage">
                                    {ac.dead.includes(station.id) ? 'No supply' : `${fmt(ac.powers[station.id])} kW · ${stage[station.id]}`}
                                </span>
                            </button>
                        </li>
                    )
                ))}
            </ol>

            <div className={`dash-flow__battery${onBattery ? ' is-active' : ''}`}>
                <span className="dash-flow__riser"><Wire vertical reverse off={!onBattery} /></span>
                <div className="dash-flow__box dash-flow__box--side dash-flow__box--static">
                    <span className="dash-flow__name">Battery, through DC/DC</span>
                    <span className="dash-flow__stage">{ac.batteryState} · {fmt(ac.soc, 0)}% state of charge</span>
                </div>
            </div>

            <DiagramKey items={[
                { mark: 'flow', label: 'Normal power path' },
                { mark: 'dash', label: 'Bypass energized / unloaded' },
                { mark: 'off', label: 'No power flowing' }
            ]} />
        </div>
    );
}

/* ── AC power quality ───────────────────────────────────────── */

export function AcPower({ sim }) {
    const { ac, setMeter } = sim;
    const [kind, setKind] = useState('voltage');

    return (
        <>
            <KpiRow>
                <Kpi label="Average line voltage" value={fmt(ac.avgLineVolts)} unit="V RMS" note={`L–L · nominal ${ac.profile.ll} V`} />
                <Kpi label="Supply frequency" value={fmt(ac.frequency, 2)} unit="Hz" note={`Nominal ${ac.profile.hz} Hz`} />
                <Kpi label="Active power" value={fmt(ac.totalKw / 1000, 3)} unit="MW" note={ac.meter.label} />
                <Kpi label="Highest voltage THD" value={fmt(ac.maxThdV, 2)} unit="%" note="Orders 2–50 · fundamental basis" />
            </KpiRow>

            <Grid cols="minmax(0, 2.1fr) minmax(0, 1fr)">
                <Panel
                    title="AC distribution path"
                    sub={`UPS path ${ac.path} · Meter locations and conversion stages`}
                    badge={<Pill tone={ac.mode === 'normal' ? 'ok' : 'crit'}>{{ normal: 'Normal double conversion', battery: 'On battery', down: 'Path out of service' }[ac.mode]}</Pill>}
                >
                    <PathDiagram ac={ac} onSelect={setMeter} />
                    <Foot>
                        Click a meter to change the measurement point. This is an illustrative conversion path with no switching controls. The gap between the incomer and the rectifier input is other load fed ahead of the UPS.
                    </Foot>
                </Panel>

                <Panel
                    title="Phase relationships"
                    actions={(
                        <Segmented
                            label="Phasor quantity"
                            value={kind}
                            onChange={setKind}
                            options={[{ value: 'voltage', label: 'V' }, { value: 'current', label: 'I' }]}
                        />
                    )}
                >
                    {ac.isDead || (kind === 'current' && !ac.totalKw) ? (
                        <Note>{ac.isDead ? 'This meter has no voltage right now, so it has no phasors to draw.' : 'This meter carries no current, so it has no current phasors. The bypass is energized and unloaded.'}</Note>
                    ) : (
                        <Phasors phases={ac.phases} kind={kind} colors={PHASE_COLOR} />
                    )}

                    <ul className="dash-phaselist">
                        {ac.phases.map(phase => (
                            <li key={phase.name}>
                                <span>
                                    <i style={{ background: PHASE_COLOR[phase.name] }} aria-hidden="true" />
                                    {phase.name} · {fmt(kind === 'current' ? phase.iAngle : phase.vAngle)}°
                                </span>
                                <strong>{kind === 'current' ? `${fmt(phase.i1)} A` : `${fmt(phase.v1)} V`}</strong>
                            </li>
                        ))}
                    </ul>
                    <Foot>L1 voltage is the 0° reference. Vector lengths are normalized within the selected quantity.</Foot>
                </Panel>
            </Grid>

            <Panel
                title="Three-phase metering"
                sub="RMS values and power at the selected meter"
                actions={<button type="button" className="dash-link" onClick={() => sim.setView('harmonics')}>Harmonic detail →</button>}
            >
                <Table>
                    <thead>
                        <tr>
                            <th scope="col">Phase</th>
                            <th scope="col" className="is-num">V L–N</th>
                            <th scope="col" className="is-num">I RMS</th>
                            <th scope="col" className="is-num">P</th>
                            <th scope="col" className="is-num">S</th>
                            <th scope="col" className="is-num">Q₁</th>
                            <th scope="col" className="is-num">True PF</th>
                            <th scope="col" className="is-num">DPF</th>
                            <th scope="col" className="is-num">THD V</th>
                            <th scope="col" className="is-num">THD I</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ac.phases.map(phase => (
                            <tr key={phase.name}>
                                <th scope="row">
                                    <i className="dash-dot" style={{ background: PHASE_COLOR[phase.name] }} aria-hidden="true" />
                                    {phase.name}
                                </th>
                                <td className="is-num">{fmt(phase.vRms)} V</td>
                                <td className="is-num">{fmt(phase.iRms)} A</td>
                                <td className="is-num">{fmt(phase.pKw)} kW</td>
                                <td className="is-num">{fmt(phase.sKva)} kVA</td>
                                <td className="is-num">{fmt(phase.q1Kvar)} kvar</td>
                                <td className="is-num">{fmt(phase.truePf, 3)}</td>
                                <td className="is-num">{fmt(phase.dpf, 3)}</td>
                                <td className="is-num">{fmt(phase.thdV, 2)}%</td>
                                <td className="is-num">{phase.thdI === null ? '—' : `${fmt(phase.thdI, 2)}%`}</td>
                            </tr>
                        ))}
                        <tr className="is-total">
                            <th scope="row">Total</th>
                            <td className="is-num">—</td>
                            <td className="is-num">—</td>
                            <td className="is-num">{fmt(ac.totalKw)} kW</td>
                            <td className="is-num">{fmt(ac.totalKva)} kVA</td>
                            <td className="is-num">{fmt(ac.totalQ)} kvar</td>
                            <td className="is-num">{fmt(ac.totalPf, 3)}</td>
                            <td className="is-num">{fmt(ac.totalDpf, 3)}</td>
                            <td className="is-num">—</td>
                            <td className="is-num">—</td>
                        </tr>
                    </tbody>
                </Table>
                <Foot>P is active power, S apparent power, Q₁ fundamental reactive power and DPF the fundamental displacement factor. True PF includes distortion. Phase currents are not summed as a load-current total.</Foot>
            </Panel>


            <Grid>
                <Panel title="Supply quality" sub="Line voltages, sequence unbalance and neutral current">
                    <Readout items={['L1–L2', 'L2–L3', 'L3–L1'].map((label, i) => ({ label, value: fmt(ac.lineVolts[i]), unit: 'V RMS' }))} />
                    <Row label="Voltage negative-sequence ratio" note="|V₂| / |V₁| · sequence-based unbalance">{fmt(ac.voltageSeq, 2)} %</Row>
                    <Row label="Current negative-sequence ratio" note="|I₂| / |I₁| · sequence-based unbalance">{ac.currentSeq === null ? '—' : `${fmt(ac.currentSeq, 2)} %`}</Row>
                    <Row label="Neutral current" note="Derived from the instantaneous sum of phase currents">{fmt(ac.neutralA)} A RMS</Row>
                    <Row label="Frequency range · 24 h" note="Meter extrema">{fmt(ac.frequencyRange[0], 2)}–{fmt(ac.frequencyRange[1], 2)} Hz</Row>
                    <Row label="Neutral-to-PE / residual current" note="Needs dedicated voltage and core-balance channels"><Pill>Not instrumented</Pill></Row>
                    <Row label="Bypass transfer permissive" note="Voltage presence alone does not establish transfer readiness"><Pill>Not supplied</Pill></Row>
                </Panel>

                <Panel title="Energy & demand" sub="Selected meter · trailing 24 hours">
                    <Readout items={[
                        { label: 'Imported energy', value: fmt(ac.importKwh / 1000, 2), unit: 'MWh', note: 'Positive-energy register' },
                        { label: 'Exported energy', value: '0.0', unit: 'kWh', note: 'Reverse flow recorded separately' },
                        { label: 'Rolling 15-minute demand', value: fmt(ac.demandKw), unit: 'kW', note: '5-minute sample boundaries' },
                        { label: 'Peak 15-minute demand', value: fmt(ac.peakDemandKw), unit: 'kW', note: 'Highest rolling window in 24 h' }
                    ]} />
                    <Foot>Derived from the DC historian using fixed conversion assumptions, not a metered billing register. These successive meters observe overlapping power flows, so their powers are not added together as separate facility loads.</Foot>
                </Panel>
            </Grid>
        </>
    );
}

/* ── Harmonics & waveform ───────────────────────────────────── */

export function Harmonics({ sim }) {
    const { ac, criteria } = sim;
    const [kind, setKind] = useState('voltage');
    const [phase, setPhase] = useState('all');
    const [order, setOrder] = useState(5);
    const [waveKind, setWaveKind] = useState('voltage');

    const shown = phase === 'all' ? PHASES : [phase];
    const shownIndex = shown.map(name => PHASES.indexOf(name));

    const groups = useMemo(() => ORDERS.map(h => ({
        key: h,
        label: `H${h}`,
        h,
        values: shown.map((name, i) => ({
            id: name,
            label: name,
            color: PHASE_COLOR[name],
            value: harmonicOf(ac, shownIndex[i], h, kind)
        }))
    })), [ac, kind, phase]); // eslint-disable-line react-hooks/exhaustive-deps

    const largest = groups.reduce((best, group) => {
        const top = Math.max(...group.values.map(v => v.value));
        return top > best.value ? { h: group.h, value: top } : best;
    }, { h: 0, value: 0 });

    const noCurrent = kind === 'current' && !ac.totalKw;

    const worstV = Math.max(...shownIndex.map(i => ac.phases[i].thdV));
    const worstI = ac.totalKw ? Math.max(...shownIndex.map(i => ac.phases[i].thdI)) : null;

    const demand = Number(criteria.demandCurrent) || null;
    const harmonicA = ac.totalKw ? Math.max(...ac.phases.map(p => p.i1 * (p.thdI / 100))) : null;
    const tdd = demand && harmonicA !== null ? (harmonicA / demand) * 100 : null;

    /* Waveform: every second sample is plenty at chart width. */
    const waveSeries = useMemo(() => {
        const pick = source => source
            .filter((_, s) => s % 2 === 0)
            .map((value, s) => [ac.wave.time[s * 2], value]);

        if (waveKind === 'neutral')
            return [{ id: 'N', label: 'Neutral', color: NEUTRAL_COLOR, points: pick(ac.wave.n) }];

        const source = waveKind === 'current' ? ac.wave.i : ac.wave.v;

        return shown.map((name, i) => ({
            id: name,
            label: name,
            color: PHASE_COLOR[name],
            points: pick(source[shownIndex[i]])
        }));
    }, [ac, waveKind, phase]); // eslint-disable-line react-hooks/exhaustive-deps

    const windowMs = ac.wave.time[ac.wave.time.length - 1];
    const waveZero = !ac.totalKw && waveKind !== 'voltage';

    const exportSpectrum = () => {
        downloadCsv(`SmartDC_Demo_Spectrum_${ac.meterTag}_${kind}.csv`, [
            ['Data mode', 'Meter', 'Quantity', 'Order', 'Frequency Hz', ...PHASES.map(n => `${n} %`), 'Order group'],
            ...ORDERS.map(h => [
                'SIMULATED', ac.meterTag, kind, h, (h * ac.frequency).toFixed(1),
                ...PHASES.map((_, i) => harmonicOf(ac, i, h, kind).toFixed(4)),
                orderGroup(h)
            ])
        ]);
        sim.notify('Spectrum exported as a CSV, marked simulated.');
    };

    return (
        <>
            <KpiRow>
                <Kpi label="Highest voltage THD" value={fmt(worstV, 2)} unit="%" note={`${phase === 'all' ? 'All phases' : phase} · harmonic / fundamental`} />
                <Kpi label="Highest current THD" value={worstI === null ? '—' : fmt(worstI, 2)} unit={worstI === null ? '' : '%'} note={worstI === null ? 'No current at this meter' : 'Present-current fundamental basis'} />
                <Kpi label="Largest harmonic" value={largest.h ? `H${largest.h}` : '—'} note={largest.h ? `${fmt(largest.value, 2)}% · ${kind} spectrum` : 'No current at this meter'} />
                <Kpi
                    label="Current demand distortion"
                    value={tdd === null ? '—' : fmt(tdd, 2)}
                    unit={tdd === null ? 'TDD' : '% TDD'}
                    note={tdd === null ? <><IL /> reference not configured</> : <>At <IL /> = {demand} A</>}
                />
            </KpiRow>

            <Panel
                title="Harmonic spectrum"
                sub="Individual harmonic content, h = 2 to 50"
                actions={(
                    <Segmented
                        label="Spectrum quantity"
                        value={kind}
                        onChange={setKind}
                        options={[{ value: 'voltage', label: 'Voltage' }, { value: 'current', label: 'Current' }]}
                    />
                )}
            >
                <div className="dash-controls">
                    <Segmented label="Phase" value={phase} onChange={setPhase} options={phaseOptions} />
                    <Select
                        label="Inspect order"
                        value={String(order)}
                        onChange={value => setOrder(Number(value))}
                        options={ORDERS.map(h => ({ value: String(h), label: `H${h}` }))}
                    />
                </div>

                {noCurrent ? (
                    <Note>This meter carries no current, so it has no current spectrum. The bypass is energized and unloaded.</Note>
                ) : (
                    <>
                        <Legend mark="bar" items={shown.map(name => ({ label: name, color: PHASE_COLOR[name] }))} />
                        <GroupedBars
                            groups={groups}
                            height={280}
                            selected={order}
                            onSelect={setOrder}
                            label={`Harmonic ${kind} spectrum at ${ac.meter.label}. The register table below lists every value.`}
                            formatY={value => `${value}%`}
                            formatTip={value => `${value.toFixed(3)}%`}
                            tickLabel={group => group.h}
                            showTick={group => group.h === 2 || group.h % 5 === 0}
                            tipTitle={group => `H${group.h} · ${fmt(group.h * ac.frequency)} Hz`}
                        />
                        <p className="dash-axis">Harmonic order · h × {fmt(ac.frequency, 2)} Hz</p>
                    </>
                )}

                <div className="dash-inspect">
                    <strong>H{order} <span>· {fmt(order * ac.frequency)} Hz</span></strong>
                    <span className="dash-inspect__values">
                        {shown.map((name, i) => (
                            <span key={name}>
                                <i style={{ background: PHASE_COLOR[name] }} aria-hidden="true" />
                                {name} <b>{harmonicOf(ac, shownIndex[i], order, kind).toFixed(3)}%</b>
                            </span>
                        ))}
                    </span>
                    <span>% of fundamental RMS</span>
                </div>
            </Panel>

            <Grid>
                <Panel
                    title="Waveform detail"
                    sub="Inspect shape, phase and distortion"
                    actions={(
                        <Segmented
                            label="Waveform quantity"
                            value={waveKind}
                            onChange={setWaveKind}
                            options={[{ value: 'voltage', label: 'V' }, { value: 'current', label: 'I' }, { value: 'neutral', label: 'N' }]}
                        />
                    )}
                >
                    {waveZero ? (
                        <Note>This meter carries no current, so there is no current or neutral waveform to draw.</Note>
                    ) : (
                        <>
                            {waveSeries.length > 1 && <Legend items={waveSeries.map(s => ({ label: s.label, color: s.color }))} />}
                            <LineChart
                                series={waveSeries}
                                height={260}
                                label={`${waveKind} waveform over two cycles`}
                                xTicks={[0, 0.25, 0.5, 0.75, 1].map(f => f * windowMs)}
                                formatX={value => value.toFixed(1)}
                                formatY={value => Math.round(value).toLocaleString('en-US')}
                                formatTip={value => `${value.toFixed(1)} ${waveKind === 'voltage' ? 'V' : 'A'}`}
                                tipTitle={value => `${value.toFixed(2)} ms`}
                                yLabel={waveKind === 'voltage' ? 'V L–N' : waveKind === 'neutral' ? 'A neutral' : 'A'}
                                xLabel="Time · ms"
                            />
                        </>
                    )}
                    <Foot>Two cycles, synthesized from the same fundamental and harmonic phasors used for RMS, power and THD. This is not a capture from connected hardware.</Foot>
                </Panel>

                <Panel title="THD, TDD & assessment basis" sub="Keep the denominator and measurement boundary visible">
                    <div className="dash-formula">
                        <h3>THD: waveform distortion</h3>
                        <p>Voltage and current THD compare the RMS harmonic sum with the fundamental RMS value at this meter.</p>
                        <code>THD = 100 × √Σ(h₂…h₅₀²) / fundamental</code>
                    </div>
                    <div className="dash-formula">
                        <h3>TDD: demand-based current distortion</h3>
                        <p>TDD uses the configured maximum-demand fundamental current <IL />, not the present current. It needs a valid demand reference and measurement boundary.</p>
                        <code>TDD = 100 × harmonic current RMS / I<sub>L</sub></code>
                    </div>
                    <Row label={<>Demand current reference <IL /></>}>
                        {demand ? `${demand} A` : (
                            <span className="dash-inline">
                                <Pill>Not configured</Pill>
                                <button type="button" className="dash-link" onClick={() => sim.setView('tests')}>Set it →</button>
                            </span>
                        )}
                    </Row>
                    <Row label="Current TDD">{tdd === null ? <Pill>Not evaluated</Pill> : `${fmt(tdd, 2)} %`}</Row>
                    <Row label="PCC compliance assessment" note="Needs a PCC definition, ISC / IL and assessment periods"><Pill>Not configured</Pill></Row>
                    <Foot>A large THD reading at low load does not by itself establish a demand-based compliance failure. No standards pass or fail is assigned here.</Foot>
                </Panel>
            </Grid>

            <Panel title="Current stress & neutral loading" sub="Waveform-derived indicators at the selected meter">
                <Readout items={[
                    { label: 'Maximum current crest factor', value: fmt(ac.crest, 3), note: 'Peak / RMS in the shown window' },
                    { label: 'Neutral RMS current', value: fmt(ac.neutralA), unit: 'A', note: 'Sum of the phase waveforms' },
                    { label: 'Neutral third harmonic', value: fmt(ac.neutralThird), unit: 'A RMS', note: 'Triplen harmonic contribution' },
                    { label: 'Phase current spread', value: fmt(ac.spread, 2), unit: '%', note: 'Maximum deviation from phase mean' }
                ]} />
            </Panel>

            <Panel title="Flicker & additional frequency bands" sub="Coverage and measurement windows">
                <Readout items={[
                    { label: 'Pst · short-term flicker', value: fmt(ac.pst, 3), note: '10-minute observation · simulated register' },
                    { label: 'Plt · long-term flicker', value: fmt(ac.plt, 3), note: '2-hour observation · 12 completed Pst windows' }
                ]} />
                <Row label="Interharmonic groups" note="Individual integer harmonics do not cover these bands"><Pill>Not instrumented</Pill></Row>
                <Row label="2–150 kHz emissions" note="Needs compatible high-frequency acquisition"><Pill>Not instrumented</Pill></Row>
                <Foot>Plt is the cubic mean of 12 completed 10-minute Pst windows. These are independent simulated registers, not calculated from the waveform above. Rapid voltage change detection is not instrumented.</Foot>
            </Panel>

            <Panel
                title="Harmonic register detail"
                sub={`${kind === 'current' ? 'Current' : 'Voltage'} RMS magnitude · percent of same-phase fundamental`}
                actions={<button type="button" className="dash-btn" onClick={exportSpectrum}>Export spectrum</button>}
            >
                <div className="dash-scroll">
                    <Table>
                        <thead>
                            <tr>
                                <th scope="col">Order</th>
                                <th scope="col" className="is-num">Frequency</th>
                                {shown.map(name => <th scope="col" className="is-num" key={name}>{name}</th>)}
                                <th scope="col">Order group</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ORDERS.map(h => (
                                <tr key={h} className={h === order ? 'is-selected' : undefined}>
                                    <th scope="row">
                                        <button type="button" className="dash-tablelink" onClick={() => setOrder(h)}>H{h}</button>
                                    </th>
                                    <td className="is-num">{fmt(h * ac.frequency)} Hz</td>
                                    {shownIndex.map(i => (
                                        <td className="is-num" key={i}>{harmonicOf(ac, i, h, kind).toFixed(4)} %</td>
                                    ))}
                                    <td>{orderGroup(h)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                <Foot>49 orders per selected phase, 2 through 50. The fundamental, h = 1, is the 100% reference.</Foot>
            </Panel>
        </>
    );
}

/* ── Power quality events ───────────────────────────────────── */

const TONE = { sag: 'warn', swell: 'warn', interruption: 'crit', transient: 'info' };

const rangeLabel = { 1: '1 hour', 6: '6 hours', 24: '24 hours' };

export function Events({ sim }) {
    const { path, anchor, ac } = sim;
    const [type, setType] = useState('all');
    const [range, setRange] = useState(24);
    const [selected, setSelected] = useState('sag');
    const [phase, setPhase] = useState('all');

    const events = useMemo(() => eventsFor(path, anchor, sim.dc.scenario), [path, anchor, sim.dc.scenario.id, Math.floor(sim.dc.scenario.elapsed)]); // eslint-disable-line react-hooks/exhaustive-deps
    const inRange = events.filter(event => event.minutes <= range * 60);
    const countOf = id => (id === 'all' ? inRange.length : inRange.filter(e => e.type === id).length);
    const listed = inRange.filter(event => type === 'all' || event.type === type);

    const event = listed.find(e => e.key === selected) || listed[0] || null;
    const isTransient = event && event.type === 'transient';

    const envelope = useMemo(() => (event ? eventEnvelope(event) : []), [event]);
    const capture = useMemo(() => (event ? eventCapture(event, ac.profile) : null), [event, ac.profile]);

    const shown = phase === 'all' ? PHASES : [phase];

    const ticks = (from, to) => [0, 0.25, 0.5, 0.75, 1].map(f => from + (to - from) * f);

    const exportEvents = () => {
        downloadCsv(`SmartDC_Demo_Events_Path${path}.csv`, [
            ['Data mode', 'Event ID', 'Type', 'Meter', 'Time UTC', 'Magnitude %', 'Duration ms', 'Phases'],
            ...listed.map(e => ['SIMULATED', e.id, e.type, e.meterLabel, new Date(e.at).toISOString(), e.magnitude, e.duration, e.phases.join(' ')])
        ]);
        sim.notify('Events exported as a CSV, marked simulated.');
    };

    const exportCapture = () => {
        downloadCsv(`SmartDC_Demo_Capture_${event.id}.csv`, [
            ['Data mode', 'Event ID', 'Time ms', 'L1 V', 'L2 V', 'L3 V'],
            ...capture.points.map(p => ['SIMULATED', event.id, p.t.toFixed(3), p.L1.toFixed(1), p.L2.toFixed(1), p.L3.toFixed(1)])
        ]);
        sim.notify('Capture exported as a CSV, marked simulated.');
    };

    return (
        <>
            <KpiRow>
                <Kpi label="Events in selected window" value={inRange.length} note={`Path ${path} · ${rangeLabel[range]}`} />
                <Kpi label="Voltage sags" value={countOf('sag')} note="RMS residual and duration retained" />
                <Kpi label="Supply interruptions" value={countOf('interruption')} note="Historical input-side records" />
                <Kpi label="Transient captures" value={countOf('transient')} note="Instantaneous peaks, on a separate basis" />
            </KpiRow>

            <div className="dash-controls dash-controls--spread">
                <div className="dash-chips" role="group" aria-label="Event type">
                    {EVENT_TYPES.map(option => (
                        <button type="button" key={option.id} aria-pressed={type === option.id} onClick={() => setType(option.id)}>
                            {option.label} <span>{countOf(option.id)}</span>
                        </button>
                    ))}
                </div>

                <div className="dash-controls__end">
                    <Segmented
                        label="Time range"
                        value={range}
                        onChange={setRange}
                        options={[{ value: 1, label: '1h' }, { value: 6, label: '6h' }, { value: 24, label: '24h' }]}
                    />
                    <button type="button" className="dash-btn" onClick={exportEvents}>Export events</button>
                </div>
            </div>

            <Grid cols="minmax(0, 1fr) minmax(0, 2.2fr)">
                <Panel title="Event log" sub={`Path ${path} · All meter locations`} badge={<Pill>{plural(listed.length, 'record')}</Pill>}>
                    {listed.length === 0 && <Note>No matching events in this window. Widen the range or choose another type.</Note>}

                    <ul className="dash-events">
                        {listed.map(item => (
                            <li key={item.key}>
                                <button type="button" aria-pressed={event && event.key === item.key} onClick={() => setSelected(item.key)}>
                                    <span className="dash-events__top">
                                        <Pill tone={TONE[item.type]}>{item.type}</Pill>
                                        <time>{clock(item.at)} ET</time>
                                    </span>
                                    <strong>{item.title}</strong>
                                    <span className="dash-events__where">{item.meterLabel} · Path {path}</span>
                                    <span className="dash-events__nums">
                                        <b>{fmt(item.magnitude)}%</b> {item.type === 'transient' ? 'peak' : 'of nominal'}
                                        <b>{item.duration < 1 ? item.duration.toFixed(3) : fmt(item.duration)} ms</b>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </Panel>

                {event ? (
                    <div className="dash-stack">
                        <Panel
                            title={event.title}
                            sub={`${event.meterLabel} · ${event.id} · Double conversion`}
                            actions={<button type="button" className="dash-btn" onClick={exportCapture}>Export capture</button>}
                        >
                            <Readout items={[
                                { label: isTransient ? 'Peak amplitude' : 'Remaining voltage', value: fmt(event.magnitude), unit: isTransient ? '% of nominal peak' : '% of nominal RMS' },
                                { label: 'Event duration', value: event.duration < 1 ? event.duration.toFixed(3) : fmt(event.duration), unit: 'ms' },
                                { label: 'Observed phases', value: event.phases.join(', ') },
                                { label: 'Source timestamp · ET', value: stamp(event.at) }
                            ]} />

                            <div className="dash-controls dash-controls--spread">
                                <Legend items={[{ label: 'Upstream input', color: PHASE_COLOR.L1 }, { label: 'UPS output', color: PHASE_COLOR.L2 }]} />
                                <Pill tone="ok">Recorded and recovered</Pill>
                            </div>

                            <LineChart
                                series={[
                                    { id: 'upstream', label: 'Upstream input', color: PHASE_COLOR.L1, points: envelope.map(p => [p.t, p.upstream]) },
                                    { id: 'output', label: 'UPS output', color: PHASE_COLOR.L2, points: envelope.map(p => [p.t, p.output]) }
                                ]}
                                height={250}
                                domain={event.scale}
                                label={`${isTransient ? 'Peak' : 'RMS'} envelope of the event, upstream against UPS output`}
                                xTicks={ticks(event.window[0], event.window[1])}
                                formatX={value => (Math.abs(value) < 10 ? value.toFixed(1) : Math.round(value))}
                                formatY={value => `${value}%`}
                                formatTip={value => `${value.toFixed(1)}%`}
                                tipTitle={value => `${value.toFixed(2)} ms`}
                                yLabel={isTransient ? 'Instantaneous peak / nominal peak' : 'RMS voltage / nominal RMS'}
                                xLabel="Time relative to event · ms"
                            />
                            <Foot>{event.note} All values are simulated.</Foot>
                        </Panel>

                        <Panel
                            title="Retained waveform capture"
                            sub="Simulated local recorder · not reconstructed from display polling"
                            badge={<Pill>{fmt(capture.windowMs, 2)} ms capture window</Pill>}
                        >
                            <div className="dash-controls">
                                <Segmented label="Capture phase" value={phase} onChange={setPhase} options={phaseOptions} />
                                <Legend items={shown.map(name => ({ label: name, color: PHASE_COLOR[name] }))} />
                            </div>

                            <LineChart
                                series={shown.map(name => ({
                                    id: name,
                                    label: name,
                                    color: PHASE_COLOR[name],
                                    points: capture.points.map(p => [p.t, p[name]])
                                }))}
                                height={250}
                                label="Instantaneous voltage waveform through the event"
                                xTicks={ticks(event.window[0], event.window[1])}
                                formatX={value => (Math.abs(value) < 10 ? value.toFixed(1) : Math.round(value))}
                                formatY={value => Math.round(value)}
                                formatTip={value => `${value.toFixed(1)} V`}
                                tipTitle={value => `${value.toFixed(2)} ms`}
                                yLabel="V L–N"
                                xLabel="Time · ms"
                            />
                            <Foot>Instantaneous voltage in volts, with zero marking the event start. The capture window is distinct from the event duration, at {fmt(capture.sampleRate / 1000)} kS/s simulated sampling.</Foot>
                        </Panel>

                        <Panel title="Upstream / UPS response" sub="Compare the disturbance with the downstream response">
                            <ol className="dash-response">
                                <li>
                                    <span>Upstream observation</span>
                                    <strong>{isTransient ? 'Transient peak detected' : `${fmt(event.magnitude)}% residual`}</strong>
                                    <p>{event.meterLabel} · {event.duration < 1 ? event.duration.toFixed(3) : fmt(event.duration)} ms</p>
                                </li>
                                <li>
                                    <span>Conversion path</span>
                                    <strong>UPS inverter path</strong>
                                    <p>Independent input and output observations</p>
                                </li>
                                <li>
                                    <span>Output observation</span>
                                    <strong>{fmt(event.output)}% minimum {isTransient ? 'envelope' : 'RMS'}</strong>
                                    <p>Retained output-meter comparison</p>
                                </li>
                            </ol>
                            <Table>
                                <thead>
                                    <tr>
                                        <th scope="col">Source</th>
                                        <th scope="col">Time basis</th>
                                        <th scope="col">Measurement basis</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th scope="row">Upstream PQ recorder</th>
                                        <td>Device timestamp · simulated</td>
                                        <td>{isTransient ? 'Instantaneous peak + waveform' : 'RMS envelope + waveform'}</td>
                                    </tr>
                                    <tr>
                                        <th scope="row">UPS output recorder</th>
                                        <td>Time-aligned demo reference</td>
                                        <td>{isTransient ? 'Peak envelope comparison' : 'RMS output envelope'}</td>
                                    </tr>
                                </tbody>
                            </Table>
                            <Foot>Correlation context: these output values are simulated examples. They do not demonstrate a completed equipment acceptance or ride-through test.</Foot>
                        </Panel>
                    </div>
                ) : (
                    <Panel title="No event selected"><Note>Choose a range and type with at least one record.</Note></Panel>
                )}
            </Grid>
        </>
    );
}
